import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Spinner } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { AttendanceReportRow } from "./types/attendance";
import {
  fetchAttendanceConfig,
  fetchBatchStudentReport,
  markAttendanceAbsent,
  markStudentPresentWithRemark,
  updateAttendanceRemark,
} from "./services/attendanceApi";
import { useToast } from "../components/Toast";
import ConfirmationModal from "../components/ConfirmationModal";
import { AttendanceReportFilterBar } from "./utils/AttendanceReportFilterBar";
import { downloadXlsx } from "./utils/downloadXlsx";
import {
  mapStudentReportRows,
  type MappedStudentReportRow,
} from "./utils/mapStudentReportRows";
import {
  ALL_FILTER,
  batchIdForName,
  reportRowBatchNames,
  uniqueSorted,
} from "./utils/attendanceReportRows";
import {
  AttendanceReportCard,
  AttendanceReportExportButton,
  AttendanceReportSearch,
  ReportSortHeader,
  reportBodyCellStyle,
  reportHeadCellStyle,
} from "./utils/attendanceReportUi";

interface StudentReportLocationState {
  reportRow?: AttendanceReportRow;
  attendanceNo?: string;
  attendanceSessionId?: string;
  batchId?: string;
  course?: string;
  batch?: string;
  date?: string;
  purpose?: string;
  returnToTestPath?: string;
}

type BatchStudentRow = MappedStudentReportRow;

type StudentCol = "studentName" | "usn" | "studentId" | "status" | "remark";

const STUDENT_COLUMNS: {
  key: StudentCol;
  label: string;
}[] = [
  { key: "studentName", label: "Student Name" },
  { key: "usn", label: "USN" },
  { key: "studentId", label: "Student Id" },
  { key: "status", label: "Status" },
  { key: "remark", label: "Remark" },
];

const studentCellValue = (row: BatchStudentRow, key: StudentCol): string => {
  if (key === "status") return row.present === "Y" ? "Present" : "Absent";
  if (key === "remark") return row.remark || "—";
  return String(row[key] ?? "");
};

const AttendanceStudentReport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { attendanceName: attendanceNameParam } = useParams<{
    attendanceName: string;
  }>();
  const attendanceName = attendanceNameParam ?? "";
  const navigationState = (location.state ?? {}) as StudentReportLocationState;
  const reportRow = navigationState.reportRow;
  const attendanceNo = navigationState.attendanceNo;
  const attendanceSessionId =
    navigationState.attendanceSessionId ?? reportRow?.attendanceSessionId;
  const headerCourse = navigationState.course ?? reportRow?.course ?? "";
  const headerBatch = navigationState.batch ?? reportRow?.batch ?? "";
  const headerDate = navigationState.date ?? reportRow?.date ?? "";

  const [course, setCourse] = useState(
    () => headerCourse || reportRow?.course || "",
  );
  const [batch, setBatch] = useState(ALL_FILTER);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<StudentCol>("studentName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [remarkOptions, setRemarkOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const { showToast } = useToast();
  const [markAbsentTarget, setMarkAbsentTarget] = useState<BatchStudentRow | null>(
    null,
  );
  const [reportMeta, setReportMeta] = useState({
    course: headerCourse,
    batch: headerBatch,
    college: "",
    overallAttendancePercent: 0,
    batchAverage: 0,
    totalStudents: 0,
  });
  const [rows, setRows] = useState<BatchStudentRow[]>([]);

  const courseOptions = useMemo(
    () => uniqueSorted([headerCourse, reportRow?.course ?? ""]),
    [headerCourse, reportRow?.course],
  );

  useEffect(() => {
    if (courseOptions.length === 0) return;
    if (!course || !courseOptions.includes(course)) {
      setCourse(courseOptions[0]);
    }
  }, [course, courseOptions]);
  const batchOptions = useMemo(
    () =>
      uniqueSorted(
        reportRow
          ? reportRowBatchNames(reportRow)
          : headerBatch.split(",").map((name) => name.trim()),
      ),
    [headerBatch, reportRow],
  );

  const selectedBatchId = useMemo(() => {
    if (batch === ALL_FILTER) return undefined;
    if (reportRow) return batchIdForName(reportRow, batch);
    return navigationState.batchId;
  }, [batch, navigationState.batchId, reportRow]);

  useEffect(() => {
    void fetchAttendanceConfig().then((config) => {
      const labels = config.remarkOptions.map((r) => r.remarkLabel);
      if (labels.length > 0) setRemarkOptions(labels);
    });
  }, []);

  const loadReport = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError("");

      if (!attendanceSessionId) {
        if (!silent) {
          setError(
            attendanceName
              ? `Missing session information for ${attendanceName}.`
              : "Missing session information for this report.",
          );
          setLoading(false);
        }
        return;
      }

      try {
        const data = await fetchBatchStudentReport(
          attendanceSessionId,
          selectedBatchId,
        );
        setRows(mapStudentReportRows(data, attendanceNo));
        setReportMeta({
          course: data.course || headerCourse,
          batch: data.batch || headerBatch,
          college: data.college,
          overallAttendancePercent: data.overallAttendancePercent,
          batchAverage: data.batchAverage,
          totalStudents: data.students.length,
        });
      } catch (err) {
        console.error("Error fetching batch student report:", err);
        setError("Failed to load student report.");
      } finally {
        if (!silent) setLoading(false);
        else setRefreshing(false);
      }
    },
    [
      attendanceName,
      attendanceNo,
      attendanceSessionId,
      headerBatch,
      headerCourse,
      selectedBatchId,
    ],
  );

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const handleRemarkChange = useCallback(
    async (row: BatchStudentRow, remark: string) => {
      if (!remark) return;
      if (!row.recordId && !attendanceSessionId) {
        setError("Cannot save remark: attendance session is missing for this report.");
        return;
      }

      setSavingRowId(row.id);
      setError("");

      try {
        const result = row.recordId
          ? await updateAttendanceRemark(row.recordId, remark)
          : await markStudentPresentWithRemark({
              attendanceSessionId: attendanceSessionId!,
              studentId: row.studentId,
              attendanceNo: row.attendanceNo,
              remarkLabel: remark,
            });
        showToast(result.message || "Student marked present with remark.");
        await loadReport({ silent: true });
      } catch (err) {
        console.error("Error updating remark:", err);
        showToast("Failed to save remark. Please try again.", { severity: "error" });
        await loadReport({ silent: true });
      } finally {
        setSavingRowId(null);
      }
    },
    [attendanceSessionId, loadReport, showToast],
  );

  const confirmMarkAbsent = useCallback(async () => {
    const row = markAbsentTarget;
    if (!row?.recordId) {
      setMarkAbsentTarget(null);
      return;
    }

    setSavingRowId(row.id);
    setError("");
    try {
      const result = await markAttendanceAbsent(row.recordId);
      showToast(result.message || "Student marked absent.");
      setMarkAbsentTarget(null);
      await loadReport({ silent: true });
    } catch (err) {
      console.error("Error marking absent:", err);
      showToast("Failed to mark student absent. Please try again.", {
        severity: "error",
      });
      setMarkAbsentTarget(null);
      await loadReport({ silent: true });
    } finally {
      setSavingRowId(null);
    }
  }, [loadReport, markAbsentTarget, showToast]);

  const visibleRows = useMemo(() => {
    let next = rows;
    if (course !== ALL_FILTER && headerCourse && headerCourse !== course) {
      next = [];
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return next;
    return next.filter((row) =>
      [
        row.studentName,
        row.usn,
        row.studentId,
        row.present === "Y" ? "present" : "absent",
        row.remark,
        row.attendanceNo,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [course, headerCourse, rows, searchQuery]);

  const displayRows = useMemo(() => {
    const sorted = [...visibleRows];
    sorted.sort((a, b) => {
      const cmp = studentCellValue(a, sortBy).localeCompare(
        studentCellValue(b, sortBy),
        undefined,
        { numeric: true, sensitivity: "base" },
      );
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [sortBy, sortDir, visibleRows]);

  const handleDownload = useCallback(() => {
    const exportRows = displayRows.map((row, index) => ({
      "Sl No": index + 1,
      "Student Name": row.studentName,
      USN: row.usn || "",
      "Student Id": row.studentId,
      Status: row.present === "Y" ? "Present" : "Absent",
      Remark: row.remark || "",
    }));
    const safeBatch = (reportMeta.batch || "batch").replace(/[^\w-]+/g, "_");
    const safeDate = (headerDate || "report").replace(/[^\w-]+/g, "_");
    downloadXlsx(
      exportRows,
      "Students",
      `attendance_${safeBatch}_${safeDate}.xlsx`,
    );
  }, [headerDate, reportMeta.batch, displayRows]);

  return (
    <>
    <AttendanceReportCard
      toolbar={
        <>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <AttendanceReportFilterBar
              courses={courseOptions}
              batches={batchOptions}
              course={course}
              batch={batch}
              disabled={loading}
              showPurpose={false}
              showCourseAll={false}
              onCourseChange={setCourse}
              onBatchChange={setBatch}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <AttendanceReportSearch
              value={searchQuery}
              onChange={setSearchQuery}
            />
            {navigationState.returnToTestPath && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(navigationState.returnToTestPath!)}
                style={{
                  height: "36px",
                  fontSize: "12px",
                  textTransform: "none",
                  border: "1px solid #ccc",
                  color: "#666",
                  whiteSpace: "nowrap",
                }}
              >
                Return to Test
              </Button>
            )}
            <AttendanceReportExportButton
              onClick={handleDownload}
              disabled={displayRows.length === 0}
            />
          </Box>
        </>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }}>
          {error}
        </Alert>
      )}
      {refreshing && (
        <Alert severity="info" sx={{ mb: 1, flexShrink: 0 }}>
          Refreshing report...
        </Alert>
      )}

      <TableContainer
        component={Paper}
        style={{
          marginTop: "8px",
          flexGrow: 1,
          overflow: "auto",
          position: "relative",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={reportHeadCellStyle()}>
                <span style={{ color: "#666" }}>Sl No</span>
              </TableCell>
              {STUDENT_COLUMNS.map((column) => (
                <TableCell key={column.key} style={reportHeadCellStyle()}>
                  <ReportSortHeader
                    label={column.label}
                    active={sortBy === column.key}
                    direction={sortDir}
                    onClick={() => {
                      if (sortBy === column.key) {
                        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      } else {
                        setSortBy(column.key);
                        setSortDir("asc");
                      }
                    }}
                  />
                </TableCell>
              ))}
              <TableCell style={reportHeadCellStyle()}>
                <span style={{ color: "#666" }}>Action</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: "20px" }}>
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </TableCell>
              </TableRow>
            ) : displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: "20px" }}>
                  No students found for this attendance
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row, index) => {
                const isPresent = row.present === "Y";
                return (
                  <TableRow
                    key={row.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                    }}
                    sx={{ "&:hover": { backgroundColor: "#f0f0f0" } }}
                  >
                    <TableCell style={reportBodyCellStyle({ textAlign: "center" })}>
                      {index + 1}
                    </TableCell>
                    <TableCell style={reportBodyCellStyle()}>
                      {row.studentName}
                    </TableCell>
                    <TableCell style={reportBodyCellStyle()}>
                      {row.usn || "—"}
                    </TableCell>
                    <TableCell style={reportBodyCellStyle()}>
                      {row.studentId}
                    </TableCell>
                    <TableCell
                      style={reportBodyCellStyle({
                        textAlign: "center",
                        fontWeight: 700,
                        color: isPresent ? "#2e7d32" : "#d32f2f",
                      })}
                    >
                      {isPresent ? "Present" : "Absent"}
                    </TableCell>
                    <TableCell style={reportBodyCellStyle()}>
                      {isPresent ? (
                        row.remark || "—"
                      ) : (
                        <FormControl
                          variant="outlined"
                          size="small"
                          style={{ minWidth: 180 }}
                        >
                          <Select
                            displayEmpty
                            value={row.remark || ""}
                            disabled={savingRowId === row.id}
                            onChange={(e) =>
                              void handleRemarkChange(row, String(e.target.value))
                            }
                            style={{ height: "28px", fontSize: "11px" }}
                          >
                            <MenuItem value="">
                              <em>Select remark</em>
                            </MenuItem>
                            {remarkOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell style={reportBodyCellStyle({ textAlign: "center" })}>
                      {isPresent ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={
                            !row.recordId || savingRowId === row.id
                          }
                          onClick={() => setMarkAbsentTarget(row)}
                          style={{
                            fontSize: "11px",
                            padding: "4px 12px",
                            minWidth: "60px",
                            textTransform: "none",
                          }}
                        >
                          Mark Absent
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </AttendanceReportCard>
      <ConfirmationModal
        show={Boolean(markAbsentTarget)}
        onHide={() => setMarkAbsentTarget(null)}
        onConfirm={() => void confirmMarkAbsent()}
        title="Mark Absent"
        message={
          markAbsentTarget
            ? `Mark ${markAbsentTarget.studentName} (${markAbsentTarget.studentId}) as absent?`
            : "Mark this student as absent?"
        }
        confirmText="Mark Absent"
        variant="danger"
        isLoading={
          Boolean(markAbsentTarget) && savingRowId === markAbsentTarget?.id
        }
      />
    </>
  );
};

export default AttendanceStudentReport;
